import React, {
  ReactElement, useMemo, useEffect, useCallback,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import Form from '../../../components/form/form.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FormContainer from '../../../components/form-container/form-container.component';
import SettingsUserInternal from '../settings-user/settings-user-internal/settings-user-internal.component';
import SettingsUserInternalValidator from '../settings-user/settings-user-internal/settings-user-internal.validator';
import FormService from '../../../services/form-service';
import useFormStructureModified from '../../../hooks/use-form-structure-modified';
import { getSettingsData } from '../settings.action';
import { getListContactCompanyData } from '../../app/search-list/search-list.action';
import { resetFormErrors } from '../../app/app.action';
import { SETTINGS_PROFILE_VIEW } from '../../../constants/entity/permissions';
import { SETTING_TYPE_USER_SETTINGS } from '../../../constants/entity/setting-types';
import {
  SETTING_NAME_DEFAULT_PORTFOLIO_FILTER_IDS, SETTING_NAME_SEARCH_RESULT_GROUPS,
} from '../../../constants/entity/setting-names';
import { TAB_NAME_PROFILE } from '../../../constants/system/tab-names';
import { FORM_ID_SETTINGS_PROFILE } from '../../../constants/system/form-ids';
import { SEARCH_RESULT_GROUP_LIST } from '../../../constants/system/search-result-groups';

const SettingsProfile: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isEditMode,
    isDataLoaded,
    isRightDrawerOpen,
    currentUser,
    actionHash,
    userSettingsExportFile,
    userApiKey,
    assetTypeList,
    userData,
    accountUserTypeList,
    contactCompanyList,
    roleList,
    routeList,
    portfolioList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const dispatch: ReduxDispatch = useDispatch();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const pageTitle: string = 'Settings: Profile';
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;

  const profileViewPermissions: string[] = useMemo((): string[] => [SETTINGS_PROFILE_VIEW], []);

  const onContactCompanySearch: Function = useCallback((query: string): void => {
    dispatch(getListContactCompanyData({
      query,
    }));
  }, []);

  const onContactCompanySearchClose: Function = useCallback((): void => {
    dispatch(getListContactCompanyData({
      query: null,
    }));
  }, []);

  const onRefresh: Function = useCallback((): void => {
    dispatch(getSettingsData());
  }, []);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    dispatch(getSettingsData());
  }, []);

  return (
    <CheckAccess permissions={profileViewPermissions}>
      <Form id={FORM_ID_SETTINGS_PROFILE} onChange={onFormChange}>
        <SettingsUserInternal
          isUserProfile
          isDataLoaded={isDataLoaded}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          currentUser={currentUser}
          pageTitle={pageTitle}
          activeTab={TAB_NAME_PROFILE}
          actionHash={actionHash}
          userSettingsExportFile={userSettingsExportFile}
          userApiKey={userApiKey}
          userData={userData}
          roleList={roleList}
          routeList={routeList}
          portfolioList={portfolioList}
          assetTypeList={assetTypeList}
          accountUserTypeList={accountUserTypeList}
          contactCompanyList={contactCompanyList}
          submitForm={submitForm}
          isFormPristine={isFormPristine}
          isFormStructureModified={isFormStructureModified}
          isFormSaving={isFormSaving}
          formError={formError}
          formProps={formProps}
          formValues={formValues}
          withFormErrors={withFormErrors}
          serverFormErrors={serverFormErrors}
          mainServerFormError={mainServerFormError}
          resetForm={resetForm}
          changeForm={changeForm}
          onRefresh={onRefresh}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
        />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    controls,
    rightDrawer,
    settings,
  } = reducers;

  const userData: User = settings.settingsData && settings.settingsData.profileSettingsData;
  const portfolioList: Portfolio[] = settings.settingsData && settings.settingsData.portfolioList;

  if (userData) {
    initialValues.photo = '';
    initialValues.firstName = userData.firstName;
    initialValues.lastName = userData.lastName;
    initialValues.companyId = userData.company ? userData.company.id : null;
    initialValues.email = userData.email;
    initialValues.phone = userData.phone;
    initialValues.phoneExtension = userData.phoneExtension;
    initialValues.phoneWork = userData.phoneWork;
    initialValues.phoneWorkExtension = userData.phoneWorkExtension;
    initialValues.streetAddress = userData.streetAddress;
    initialValues.location = userData.location;
    initialValues.postalCode = userData.postalCode;
    initialValues.addressDetails = userData.addressDetails;
    initialValues.role = userData.role;
    initialValues.title = userData.title;
    initialValues.assetTypes = userData.assetTypes;
    initialValues.bio = userData.bio;
    initialValues.address = userData.address;
    initialValues.accountUserType = userData.accountUserType;
    initialValues.accountUserTypeGroup = userData.accountUserTypeGroup;
    initialValues.isPhoneVerified = userData.isPhoneVerified;
    initialValues.oldPassword = '';
    initialValues.password = '';
    initialValues.passwordConfirm = '';

    initialValues.userSettings = {};
    if (userData.settings && userData.settings.length) {
      userData.settings.forEach((userSetting: Setting): void => {
        if (userSetting.type === SETTING_TYPE_USER_SETTINGS) {
          let initialUserSettingValue: SettingValue | null;

          if (userSetting.name === SETTING_NAME_DEFAULT_PORTFOLIO_FILTER_IDS) {
            initialUserSettingValue = (
              !!portfolioList && !!portfolioList.length && !!userSetting.value && !!userSetting.value.length
              && userSetting.value.map((portfolioId: number): Portfolio | null => (
                portfolioList.find((portfolioItem: Portfolio): boolean => (
                  portfolioItem.id === portfolioId
                )) || null
              )).filter((portfolioItem: Portfolio | null): boolean => !!portfolioItem)
            );
          } else if (userSetting.name === SETTING_NAME_SEARCH_RESULT_GROUPS) {
            initialUserSettingValue = (
              !!userSetting.value && !!userSetting.value.length
              && userSetting.value.map((searchResultGroupName: string): SearchResultGroup | null => (
                SEARCH_RESULT_GROUP_LIST.find((searchResultGroupItem: SearchResultGroup): boolean => (
                  searchResultGroupItem.name === searchResultGroupName
                )) || null
              )).filter((searchResultGroupItem: SearchResultGroup | null): boolean => !!searchResultGroupItem)
            );
          } else {
            initialUserSettingValue = userSetting.value;
          }

          initialValues.userSettings[userSetting.name] = {
            value: initialUserSettingValue,
          };
        }
      });
    }
  }

  return {
    isDataLoaded: !!settings.settingsData,
    isEditMode: controls.isEditMode,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    currentUser: app.baseData && app.baseData.currentUser,
    userSettingsExportFile: settings.userSettingsExportFile,
    userApiKey: settings.userApiKey,
    actionHash: settings.actionHash,
    assetTypeList: settings.settingsData && settings.settingsData.assetTypeList,
    roleList: settings.settingsData && settings.settingsData.roleList,
    routeList: settings.settingsData && settings.settingsData.routeList,
    portfolioList: settings.settingsData && settings.settingsData.portfolioList,
    userData: settings.settingsData && settings.settingsData.profileSettingsData,
    accountUserTypeList: settings.settingsData && settings.settingsData.accountUserTypeList,
    contactCompanyList: settings.settingsData && settings.settingsData.contactCompanyList,
  };
};

const SettingsProfileForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ settings }: Reducers): ValidationErrorsMap => (
    settings.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ settings }: Reducers): ValidationErrorsMap => (
    settings.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_SETTINGS_PROFILE}
      component={SettingsProfile}
      validator={SettingsUserInternalValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(SettingsProfileForm);
